// Pure logic for the World Map (scratch map) feature: destination-string →
// country inference, visited-list toggling, stats, and trip-based suggestions.
// No React, no I/O — everything here is unit-tested in __tests__/visited.test.js.
import { COUNTRIES, COUNTRY_COUNT, countryForA2 } from '../data/countries';

// Cities the app knows as trip destinations (DEST_IDEAS keys + DEST_ALIASES
// variants in data.js, plus sample-trip cities) → home country. Lets a bare
// "Kyoto" or "Porto" destination still suggest the right country.
const CITY_TO_COUNTRY = {
  lisbon: 'PT', porto: 'PT', sintra: 'PT',
  barcelona: 'ES', madrid: 'ES', seville: 'ES', valencia: 'ES',
  paris: 'FR', lyon: 'FR', nice: 'FR', marseille: 'FR',
  tokyo: 'JP', kyoto: 'JP', osaka: 'JP',
  rome: 'IT', florence: 'IT', milan: 'IT', naples: 'IT', venice: 'IT',
  amsterdam: 'NL',
  bali: 'ID', ubud: 'ID', seminyak: 'ID', canggu: 'ID', kuta: 'ID', jimbaran: 'ID', uluwatu: 'ID',
  bangkok: 'TH', 'chiang mai': 'TH', phuket: 'TH',
  london: 'GB',
  'new york': 'US', brooklyn: 'US', manhattan: 'US', queens: 'US', bronx: 'US',
  seoul: 'KR', incheon: 'KR', busan: 'KR',
  prague: 'CZ', praha: 'CZ',
  vienna: 'AT', wien: 'AT',
  ljubljana: 'SI',
  zagreb: 'HR',
  budapest: 'HU',
  istanbul: 'TR',
  oaxaca: 'MX', 'mexico city': 'MX',
  'reykjavik': 'IS', 'reykjavík': 'IS',
  singapore: 'SG',
  'hong kong': 'HK',
};

let aliasIndex = null;
function getAliasIndex() {
  if (aliasIndex) return aliasIndex;
  aliasIndex = new Map();
  for (const c of COUNTRIES) {
    aliasIndex.set(c.name.toLowerCase(), c.a2);
    for (const a of c.aliases) if (!aliasIndex.has(a)) aliasIndex.set(a, c.a2);
  }
  return aliasIndex;
}

/**
 * Infer an ISO alpha-2 country code from a trip destination string like
 * "Paris, France", "Lisbon" or "Seoul, South Korea". Segments are checked
 * right-to-left (country usually comes last), then known cities. Null when
 * nothing matches — never guess.
 */
export function inferCountryFromDestination(destination) {
  if (!destination || typeof destination !== 'string') return null;
  const segments = destination.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const idx = getAliasIndex();
  for (let i = segments.length - 1; i >= 0; i--) {
    if (idx.has(segments[i])) return idx.get(segments[i]);
  }
  for (let i = segments.length - 1; i >= 0; i--) {
    if (CITY_TO_COUNTRY[segments[i]]) return CITY_TO_COUNTRY[segments[i]];
  }
  return null;
}

/** Toggle a country in the visited list; returns a new array. */
export function toggleCountry(visited, a2) {
  return visited.includes(a2) ? visited.filter(v => v !== a2) : [...visited, a2];
}

/** Count + percentage for the stats header. Ignores codes not on the map. */
export function computeStats(visited) {
  const count = visited.filter(a2 => countryForA2(a2)).length;
  return { count, total: COUNTRY_COUNT, pct: Math.round((count / COUNTRY_COUNT) * 100) };
}

/** True when a trip is in the past (ended before today, or marked past). */
export function isPastTrip(trip, today = new Date()) {
  if (!trip) return false;
  if (trip.status === 'past') return true;
  const end = trip.dateEnd || trip.dateStart;
  if (!end) return false;
  const d = new Date(`${end}T23:59:59`);
  return !Number.isNaN(d.getTime()) && d < today;
}

/**
 * Countries to suggest scratching: inferred from past trips, not yet visited,
 * not dismissed. Returns country rows (deduped, in trip order).
 */
export function suggestedCountries(trips, visited, dismissed = [], today = new Date()) {
  const out = [];
  const seen = new Set([...visited, ...dismissed]);
  for (const trip of trips || []) {
    if (!isPastTrip(trip, today)) continue;
    const a2 = inferCountryFromDestination(trip.destination);
    if (!a2 || seen.has(a2)) continue;
    seen.add(a2);
    const row = countryForA2(a2);
    if (row) out.push(row);
  }
  return out;
}
